import * as React from 'react';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { API } from 'aws-amplify';
import * as mutations from '../../../graphql/mutations';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';

declare const global: {
  me: {
    id: string;
    orgID: string;
  };
};

interface CreateMerchantFormProps {
  onMerchantCreated: (merchantId: string) => void;
}

export function CreateMerchantForm({
  onMerchantCreated,
}: CreateMerchantFormProps) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  const validate = (): boolean => {
    const newErrors: { name?: string; email?: string } = {};

    if (!name || name.trim().length < 2) {
      newErrors.name = 'Name is required (at least 2 characters)';
    } else if (name.trim().length > 32) {
      newErrors.name = 'Name is too long (max 32 characters)';
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error('Please fix all errors and try again');
      return;
    }

    setIsLoading(true);

    try {
      const merchant = {
        orgID: global.me.orgID,
        name: name.trim(),
        email: email.trim(),
      };

      const createMerchantRes = await API.graphql({
        // @ts-ignore
        query: mutations.createPayEngineMerchant,
        variables: {
          request: JSON.stringify(merchant),
        },
      });

      // @ts-ignore
      const result = JSON.parse(createMerchantRes.data.createPayEngineMerchant);

      if (result.statusCode && result.statusCode !== 200) {
        console.error('Lambda returned an error:', result.body);
        toast.error('There was an error creating the merchant profile');
        setIsLoading(false);
        return;
      }

      const newMerchantId = JSON.parse(result?.body)?.data?.id;

      if (newMerchantId) {
        toast.success(
          'Merchant profile created successfully. You may now fill in the PayEngine onboarding form.'
        );
        onMerchantCreated(newMerchantId);
      } else {
        toast.error('There was an error creating the merchant profile');
      }
    } catch (err) {
      console.error('createMerchant(): error creating merchant:', err);
      toast.error('There was an error creating the merchant profile');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center w-full px-4 sm:px-6 py-8 sm:py-12">
      <div className="w-full max-w-[708px]">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
          Create PayEngine Merchant Profile
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground mb-8">
          In order to enable payments, you must create a merchant profile with
          PayEngine for your org with this form and then fill in the onboarding
          form.
        </p>

        <div className="border border-border rounded-xl overflow-hidden mb-6">
          <div className="bg-muted px-4 py-3 border-b border-border">
            <h3 className="text-base font-semibold text-foreground">About</h3>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Name <span className="text-destructive">*</span>
              </label>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) {
                    setErrors((prev) => ({ ...prev, name: undefined }));
                  }
                }}
                placeholder="Enter merchant name"
                className={cn(errors.name && 'border-destructive')}
              />
              {errors.name && (
                <p className="text-xs text-destructive mt-1">{errors.name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Email <span className="text-destructive">*</span>
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) {
                    setErrors((prev) => ({ ...prev, email: undefined }));
                  }
                }}
                placeholder="Enter email address"
                className={cn(errors.email && 'border-destructive')}
              />
              {errors.email && (
                <p className="text-xs text-destructive mt-1">{errors.email}</p>
              )}
            </div>
          </div>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          className="h-12 px-6 font-bold"
        >
          {isLoading ? <Loader2 className="size-5 animate-spin mr-2" /> : null}
          Create profile
        </Button>
      </div>
    </div>
  );
}
